<script lang="ts">
  import type { ApiField } from '../data-tables-api';
  import countries from '../countries.json';
  import { session } from '../session';
  import LinkedRecordDisplay from './LinkedRecordDisplay.svelte';
  import { evaluateFormula } from './formula';
  import {
    formatCheckbox,
    formatCurrencyCents,
    formatDate,
    formatDateTime,
    formatNumber,
    formatPercent,
    formatText,
    isEmpty,
    maskEmail,
    maskPhone,
    parseTags,
  } from './format';

  let {
    field,
    value,
    record,
    plainLinks = false,
  }: {
    field: ApiField;
    value: unknown;
    /** Запис цілком — потрібен для type='formula' (substitution {fieldKey}).
     *  Для інших типів ігнорується. */
    record?: Record<string, unknown>;
    /** У таблиці email/phone рендеряться як простий текст (без mailto/tel),
     *  щоб клік відкривав картку запису, а не поштовий клієнт. */
    plainLinks?: boolean;
  } = $props();

  /** Обчислений результат formula, якщо застосовно. */
  const formulaResult = $derived.by<number | null>(() => {
    if (field.type !== 'formula') return null;
    if (!field.formula_expression) return null;
    return evaluateFormula(field.formula_expression, record ?? {});
  });

  /** READER бачить email/phone у маскованому вигляді (defense-in-depth поверх
   *  backend-маски для built-in полів та для custom email/phone, які backend
   *  не маскує бо вони у jsonb). */
  const isReader = $derived($session?.role === 'READER');
  const displayEmail = $derived(
    isReader && typeof value === 'string' ? maskEmail(value) : value != null ? String(value) : '',
  );
  const displayPhone = $derived(
    isReader && typeof value === 'string' ? maskPhone(value) : value != null ? String(value) : '',
  );

  const countryByCode: Record<string, { name: string; flag: string }> = Object.fromEntries(
    countries.map((c) => [c.code, { name: c.name, flag: c.flag }]),
  );

  /** Tooltip для stat-поля з описом агрегації. */
  function statTitle(f: ApiField): string {
    const c = f.stat_config;
    if (!c) return 'stat';
    return c.aggregator === 'count'
      ? `${c.aggregator}(${c.target_slug} WHERE ${c.link_field}=record.id)`
      : `${c.aggregator}(${c.target_slug}.${c.target_field} WHERE ${c.link_field}=record.id)`;
  }
</script>

{#if field.type === 'formula'}
  {#if formulaResult === null}
    <span class="text-[var(--color-text-muted)] italic">ƒx</span>
  {:else}
    <span class="tabular-nums" title={field.formula_expression ?? ''}>
      <span class="text-[10px] text-[var(--color-text-muted-2)] mr-0.5">ƒx</span>
      {new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 }).format(formulaResult)}
    </span>
  {/if}
{:else if isEmpty(value)}
  <span class="text-[var(--color-text-muted)]">—</span>
{:else if field.type === 'email'}
  {#if isReader || plainLinks}
    <span>{displayEmail}</span>
  {:else}
    <a href={`mailto:${String(value)}`} class="text-[var(--color-pu)] hover:underline">
      {displayEmail}
    </a>
  {/if}
{:else if field.type === 'phone'}
  {#if isReader || plainLinks}
    <span>{displayPhone}</span>
  {:else}
    <a href={`tel:${String(value)}`} class="text-[var(--color-pu)] hover:underline">
      {displayPhone}
    </a>
  {/if}
{:else if field.type === 'url'}
  <a href={String(value)} target="_blank" rel="noopener noreferrer" class="text-[var(--color-pu)] hover:underline">
    {String(value)}
  </a>
{:else if field.type === 'currency'}
  <span>{formatCurrencyCents(value)}</span>
{:else if field.type === 'number' || field.type === 'auto_number'}
  <span>{formatNumber(value)}</span>
{:else if field.type === 'stat'}
  <span class="tabular-nums" title={statTitle(field)}>
    <span class="text-[10px] text-[var(--color-text-muted-2)] mr-0.5">Σ</span>
    {formatNumber(value)}
  </span>
{:else if field.type === 'percent'}
  <span>{formatPercent(value)}</span>
{:else if field.type === 'date'}
  <span>{formatDate(value)}</span>
{:else if field.type === 'datetime'}
  <span>{formatDateTime(value)}</span>
{:else if field.type === 'checkbox'}
  <span class={value ? 'text-emerald-600' : 'text-[var(--color-text-muted)]'}>{formatCheckbox(value)}</span>
{:else if field.type === 'country'}
  {#if countryByCode[String(value).toUpperCase()]}
    {@const c = countryByCode[String(value).toUpperCase()]}
    <span>{c.flag} {c.name}</span>
  {:else}
    <span>{String(value)}</span>
  {/if}
{:else if field.type === 'tags'}
  {@const tags = parseTags(value)}
  {#if tags.length === 0}
    <span class="text-[var(--color-text-muted)]">—</span>
  {:else}
    <span class="inline-flex flex-wrap gap-1">
      {#each tags as t (t)}
        <span class="rounded-full bg-[var(--color-bg-3)] px-2 py-0.5 text-[11px]">{t}</span>
      {/each}
    </span>
  {/if}
{:else if field.type === 'linked_record'}
  <!-- У таблиці (plainLinks) — dense-бульбашки з меншим overflow-порогом. -->
  <LinkedRecordDisplay {field} {value} dense={plainLinks} max={plainLinks ? 3 : 8} />
{:else if field.type === 'multi_line'}
  <span class="whitespace-pre-wrap">{String(value)}</span>
{:else}
  <span>{formatText(value)}</span>
{/if}
